import "./FormOneDevolucao.css"

import { Steps } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { connect } from "react-redux"
import { bindActionCreators, type Dispatch } from "redux"
import { v4 as uuid } from "uuid"

import ConfirmScreen from "../../components/ConfirmScreen"
import CarInfo from "../../components/FormOneDevolucao/CarInfo"
import ClientInfo from "../../components/FormOneDevolucao/ClientInfo"
import ConsultantInfo from "../../components/FormOneDevolucao/ConsultantInfo"
import ImagesForm from "../../components/FormOneDevolucao/ImagesForm"
import ProblemOrMissingItemsForm from "../../components/FormOneDevolucao/ProblemOrMissingItemsForm"
import Signatures from "../../components/FormOneDevolucao/Signatures"
import { type ApplicationState } from "../../store"
import OneFleetFormActions from "../../store/ducks/onefleetdevolucao/actions"
import { type SearchPlateSagaPayload } from "../../store/ducks/onefleetdevolucao/sagas"
import { type OneFleetDevolucaoForm, type OneFleetDevolucaoFormsState } from "../../store/ducks/onefleetdevolucao/types"

interface StateProps {
  onefleet: OneFleetDevolucaoFormsState
}

interface DispatchProps {
  setData: (payload: Partial<OneFleetDevolucaoForm>) => void
  clearData: () => void

  resetBooking: () => void

  PlateData: (data: SearchPlateSagaPayload) => void
}

type Props = StateProps & DispatchProps

function mapStateToProps (state: ApplicationState): StateProps {
  return ({
    onefleet: state.onefleetdevolucao
  })
}

function mapDispatchToProps (dispatch: Dispatch) {
  return bindActionCreators(OneFleetFormActions, dispatch)
}

const FormOneDevolucao = (props: Partial<Props>): React.ReactElement => {
  const { setData, PlateData, onefleet } = props as Props

  const { executeRecaptcha } = process.env.NODE_ENV === "production" && process.env.REACT_APP_MY_ENV === "production" ? useGoogleReCaptcha() : { executeRecaptcha: async () => process.env.REACT_APP_DEV_TOKEN as string }

  const [currentStep, setCurrentStep] = useState<number>(0)
  const [finished, setFinished] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  function stepStatus (myStatus: number) {
    return currentStep > myStatus ? "finish" : (currentStep === myStatus ? "process" : "wait")
  }

  const handleVerifyRecaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      throw new Error("Recaptcha has not been loaded")
    }

    const token = await executeRecaptcha("homepage")

    return token
  }, [executeRecaptcha])

  useEffect(() => {
    if (executeRecaptcha) setLoading(false)
  }, [executeRecaptcha])

  useEffect(() => {
    if (props.setData) props.setData({ formId: uuid() })
  }, [])

  useEffect(() => () => {
    if (props.clearData) props.clearData()
  }, [])

  return (
    <div className="view">
      {
        loading
          ? <></>
          : (
            <div className="container">
              <div className="onefleet-devolucao-steps-container">
                <div className="disappear-mobile onefleet-steps">
                  <Steps
                    // type="navigation"
                    size="small"
                    current={currentStep}
                    onChange={(value) => { setCurrentStep(value) }}
                    items={[
                      { title: "Dados do cliente", status: stepStatus(0), disabled: currentStep < 0 || finished },
                      { title: "Dados do Concessionário", status: stepStatus(1), disabled: currentStep < 1 || finished },
                      { title: "Dados do veículo", status: stepStatus(2), disabled: currentStep < 2 || finished },
                      { title: "Avarias e Itens Faltantes", status: stepStatus(3), disabled: currentStep < 3 || finished || !props.onefleet?.data.car?.hasDamageOrMissingItems },
                      { title: "Fotos do Veículo", status: stepStatus(4), disabled: currentStep < 4 || finished },
                      { title: "Assinaturas", status: stepStatus(5), disabled: currentStep < 5 || finished },
                      { title: "Revisão", status: currentStep >= 6 ? "finish" : "wait", disabled: true }
                    ]}
                  />
                </div>
              </div>
              <div className="form-area-devolucao" id="form-area-devolucao">
                {
                  currentStep === 0 && (<ClientInfo
                    defaults={onefleet}
                    afterFinish={(payload) => {
                      setData({ client: payload })
                      setCurrentStep(1)
                    }} />)
                }
                {
                  currentStep === 1 && (<ConsultantInfo
                    defaults={onefleet}
                    handleReturn={() => { setCurrentStep(0) }}
                    afterFinish={(payload) => {
                      setData({ consultant: payload })
                      setCurrentStep(2)
                      setFinished(false)
                      handleVerifyRecaptcha().catch(err => { console.warn(err) })
                    }} />)
                }
                {
                  currentStep === 2 && (<CarInfo
                    handleReturn={() => { setCurrentStep(1) }}
                    afterFinish={(payload) => {
                      setData({ car: payload })

                      if (payload.hasDamageOrMissingItems)setCurrentStep(3)
                      else setCurrentStep(4)
                    }}
                    onPlateChange={(plate: string) => {
                      handleVerifyRecaptcha().then(recaptchaToken => {
                        PlateData({
                          plate,
                          recaptchaToken
                        })
                      }).catch(err => { console.warn(err) })
                    }}
                    defaults={onefleet}
                  />)
                }
                {
                  currentStep === 3 && (<ProblemOrMissingItemsForm
                    handleReturn={() => { setCurrentStep(2) }}
                    defaults={onefleet}
                    afterFinish={(payload) => {
                      setData({ damagedOrMissingItems: payload })
                      setCurrentStep(4)
                    }}
                  />)
                }
                {
                  currentStep === 4 && (<ImagesForm
                    handleReturn={() => { setCurrentStep(3) }}
                    defaults={onefleet}
                    afterFinish={(payload) => {
                      setData({ images: payload, terms: payload.terms })
                      setCurrentStep(5)
                    }}
                  />)
                }
                {
                  currentStep === 5 && (
                    <Signatures
                    defaults={onefleet}
                    handleReturn={() => { setCurrentStep(4) }}
                    afterFinish={(payload) => {
                      setData({ signatures: payload })
                      setCurrentStep(6)
                    }}
                    />)
                }
                {
                  currentStep === 6 && (
                    <ConfirmScreen formData={onefleet} type="onefleetdevolucao" saveLocation={"/reports"}
                    handleReturn={() => { setCurrentStep(5) }}
                      afterConfirm={() => {
                        setFinished(true)
                      }}
                    />)
                }
              </div>
            </div>
            )
      }
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(FormOneDevolucao)
